import get from 'lodash/get'
import React, { useMemo, useState } from 'react'

import styled from 'styled-components'
import Dropdown, { OptionType } from '../Dropdown'
import { RendererOptions } from '../DataTable'
import { Typography } from '@material-ui/core'
import { startCase } from 'lodash'
import { useQuery } from 'urql'
import PhysicalStatusIcon from '../../assets/public/v2/location.svg'
import { Icon } from './RendererHoc'
import { RendererRoot } from './RendererHoc'

const StyledText = styled(Typography)`
  font-size: inherit;
  color: ${(props) => props.theme.palette.text.primary};

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const ICON_ENUM_MAP: Record<string, string> = {
  physicalStatus: PhysicalStatusIcon,
}

function OptionsRenderer<T extends Record<string, any>>({
  anchorEl: outerAnchorEl,
  identifier,
  name: columnName,
  rowData,
  value: externalValue,
  icon,
  onSubmit,
  editable,
}: RendererOptions<T>) {
  const [{ data }] = useQuery({
    query: OptionsQuery,
  })

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(outerAnchorEl)
  const [search, setSearch] = React.useState((externalValue as string) || '')
  const open = Boolean(anchorEl)

  const options = useMemo(
    () =>
      (data?.['v2PhysicalStatusList'] || []).map((en: any) => ({
        label: en.description || startCase(en.name),
        value: en.name,
        icon: ICON_ENUM_MAP[identifier],
      })),
    [data?.['v2PhysicalStatusList']]
  )

  const rendererValue = get(rowData, identifier)

  const optionValueOption: OptionType = useMemo(
    () =>
      options.find((opt: OptionType) => opt.value === rendererValue) || {
        label: startCase(rendererValue),
        value: rendererValue,
        icon: icon,
      },
    [rendererValue]
  )

  const [_value, setValue] = useState<OptionType>(optionValueOption)
  const value = options.find((opt: OptionType) => opt.value === externalValue) || _value

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    editable && setAnchorEl(event.currentTarget)
  }

  const handleOnChange = (value: OptionType) => {
    setValue(value)
    onSubmit?.({ slug: rowData.slug, [identifier]: value.value } as any)
    handleClose()
  }

  return (
    <>
      <RendererRoot
        role='button'
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        data-disabled={rowData.isArchived}
      >
        <Icon src={ICON_ENUM_MAP[identifier]} />
        <StyledText title={value?.label}>{value?.label || '--'}</StyledText>
      </RendererRoot>
      <Dropdown
        title={columnName}
        search={search}
        onSearch={(value) => setSearch(value)}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        options={options}
        onSelect={handleOnChange}
        value={value?.value}
      />
    </>
  )
}

const OptionsQuery = `
  query PhysicalStatusListQuery {
    v2PhysicalStatusList {
      slug,
      name
    }
  }
`

OptionsRenderer.operators = ['eq', 'neq', 'is_null', 'not_null']

export default OptionsRenderer
